import React from "react";

// region Constants
/** Start and end angle of the arc, in degrees. */
const START_ANGLE = 205;
const END_ANGLE = -25;
const CENTER_X = 110;
const CENTER_Y = 104;
const RADIUS = 76;
const STROKE_WIDTH = 27;

const BAND_OK = "#57bb8a";
const BAND_WARNING = "#fdb56b";
const BAND_CRITICAL = "#e8705f";
// endregion Constants

// region Interfaces
export interface GaugeCardProps {
  title: string;
  subtitle: string;
  unit: string;
  value: number | null;
  formattedValue: string;
  /** `true` only for km/L, where the good band sits at the TOP of the scale */
  higherIsBetter: boolean;
  median: number;
  warn: number;
  critical: number;
  /** Already translated status line; `null` when the indicator has no value in the current filter */
  status: { color: string; text: string } | null;
  /** Shown in place of the status when the indicator has no value */
  noDataText: string;
  formatTick: (value: number) => string;
}

interface Point {
  x: number;
  y: number;
}
// endregion Interfaces

const polarPoint = (radius: number, angle: number): Point => {
  const radians = (angle * Math.PI) / 180;

  return { x: CENTER_X + (radius * Math.cos(radians)), y: CENTER_Y - (radius * Math.sin(radians)) };
};

const arcPath = (radius: number, fromAngle: number, toAngle: number): string => {
  const from = polarPoint(radius, fromAngle);
  const to = polarPoint(radius, toAngle);
  const largeArc = Math.abs(toAngle - fromAngle) > 180 ? 1 : 0;

  return `M ${from.x.toFixed(2)} ${from.y.toFixed(2)} A ${radius} ${radius} 0 ${largeArc} 1 ${to.x.toFixed(2)} ${to.y.toFixed(2)}`;
};

/**
 * Speedometer-style gauge of one indicator.
 *
 * The scale is built from the alert limits and the current value — never from the worst machine of
 * the fleet, which would squeeze every reading into the first degrees of the arc. The colored bands
 * follow the direction of the indicator, so on km/L the green band sits on the high end.
 */
const GaugeCard: React.FC<GaugeCardProps> = ({
  title, subtitle, unit, value, formattedValue, higherIsBetter, median, warn, critical, status, noDataText, formatTick
}) => {

  const domainHigh = Math.max(critical, warn, value ?? 0, median) * 1.35;
  const domain = { low: 0, high: Math.max(Math.ceil(domainHigh * 2) / 2, 1) };

  const clamp = (candidate: number): number => Math.min(domain.high, Math.max(domain.low, candidate));
  const angleOf = (candidate: number): number => (
    START_ANGLE - (((clamp(candidate) - domain.low) / (domain.high - domain.low)) * (START_ANGLE - END_ANGLE))
  );

  const bands: [number, number, string][] = higherIsBetter
    ? [[domain.low, critical, BAND_CRITICAL], [critical, warn, BAND_WARNING], [warn, domain.high, BAND_OK]]
    : [[domain.low, warn, BAND_OK], [warn, critical, BAND_WARNING], [critical, domain.high, BAND_CRITICAL]];

  const needleAngle = value !== null ? angleOf(value) : null;
  const needlePoints = needleAngle === null ? null : [
    polarPoint(RADIUS - 4, needleAngle),
    polarPoint(7, needleAngle + 90),
    polarPoint(7, needleAngle - 90)
  ].map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");

  const medianInner = polarPoint(RADIUS - (STROKE_WIDTH / 2), angleOf(median));
  const medianOuter = polarPoint(RADIUS + (STROKE_WIDTH / 2), angleOf(median));

  return (
    <div className="gauge">
      <h3>{title}</h3>
      <div className="gauge-subtitle">{subtitle}</div>
      <svg viewBox="0 0 220 158" role="img" aria-label={`${title}: ${formattedValue} ${unit}`}>
        <path d={arcPath(RADIUS, START_ANGLE, END_ANGLE)} fill="none" stroke="#eceff1" strokeWidth={STROKE_WIDTH} />
        {bands
          .filter(([from, to]) => clamp(to) > clamp(from))
          .map(([from, to, color]) => (
            <path
              key={`${color}-${from}`}
              d={arcPath(RADIUS, angleOf(from), angleOf(to))}
              fill="none"
              stroke={color}
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="butt"
            />
          ))}
        {[warn, critical]
          .filter((limit) => limit > domain.low && limit < domain.high)
          .map((limit) => {
            const inner = polarPoint(RADIUS - (STROKE_WIDTH / 2), angleOf(limit));
            const outer = polarPoint(RADIUS + (STROKE_WIDTH / 2), angleOf(limit));

            return (
              <line key={`limit-${limit}`} x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y} stroke="#fff" strokeWidth={1.5} />
            );
          })}
        <line
          x1={medianInner.x}
          y1={medianInner.y}
          x2={medianOuter.x}
          y2={medianOuter.y}
          stroke="#37474f"
          strokeWidth={2}
          opacity={0.55}
        />
        {[domain.low, domain.high].map((tick) => {
          const position = polarPoint(RADIUS + (STROKE_WIDTH / 2) + 9, angleOf(tick));

          return (
            <text key={`tick-${tick}`} x={position.x.toFixed(1)} y={(position.y + 3.5).toFixed(1)} textAnchor="middle" fontSize="9.5" fill="#9aa0a6">
              {formatTick(tick)}
            </text>
          );
        })}
        {needlePoints && (
          <>
            <polygon points={needlePoints} fill="#37474f" />
            <circle cx={CENTER_X} cy={CENTER_Y} r={6.5} fill="#37474f" />
            <circle cx={CENTER_X} cy={CENTER_Y} r={2.6} fill="#fff" />
          </>
        )}
      </svg>
      <div className="gauge-value">
        {formattedValue}
        {value !== null && <span className="unit">{unit}</span>}
      </div>
      <div className="gauge-status">
        {status && <i style={{ background: status.color }} />}
        {status ? status.text : noDataText}
      </div>
    </div>
  );
};

export default GaugeCard;
